import { fromMinor, percentOf, roundHalfAwayFromZero } from './money';
/**
 * Computing TDS and TCS.
 *
 * Small enough to inline anywhere, which is exactly why it should not be. The
 * two functions below differ ONLY in their base and their rounding, and both
 * differences are the kind that produce a figure nobody can reconcile six
 * months later:
 *
 *   - TDS is deducted on the value EXCLUDING GST wherever GST is shown
 *     separately on the invoice (CBDT Circular 23/2017). Deducting on the
 *     GST-inclusive total over-deducts by 18% of the rate, every time.
 *   - TCS on sale of goods was collected on consideration INCLUDING GST
 *     (Circular 17/2020), because it attaches to money received rather than to
 *     value supplied.
 *
 * Using one base for both is the single most common way to get this wrong, and
 * it is invisible: the figures look plausible and reconcile against nothing.
 */
/**
 * The base TDS is deducted on: taxable value after every discount, before GST.
 *
 * `taxableTotal` from `computeDocument` is exactly that figure - net of both
 * the line and document discounts, and ex-tax - so this is a rename rather
 * than a calculation. It exists as a named function anyway, because the whole
 * risk here is somebody reaching for `total` instead, and a call site that
 * reads `tdsBaseFor(doc)` is one a reviewer can check.
 *
 * Shipping is included: it is part of the consideration for the supply, and it
 * is in the value the invoice is raised for.
 */
export function tdsBaseFor(doc) {
    return fromMinor(doc.taxableTotal.minor + doc.shippingCharge.minor, doc.taxableTotal.currency);
}
/**
 * The base TCS is collected on: everything the buyer owes, GST included.
 *
 * Which is the document total BEFORE TCS itself - collecting TCS on TCS is
 * circular, and no section asks for it. Subtracting rather than reading
 * `total` directly, so this stays correct on a document that already carries
 * TCS as well as one that does not.
 */
export function tcsBaseFor(doc) {
    return fromMinor(doc.total.minor - (doc.tcs?.minor ?? 0), doc.total.currency);
}
/**
 * Tax withheld at a rate, rounded to the nearest whole rupee.
 *
 * ── WHY WHOLE RUPEES ──
 * TDS and TCS are deposited, and reported on the quarterly return, in whole
 * rupees. A deduction carrying paise cannot be paid across as computed, so the
 * deductor rounds - and if Fint keeps the paise, every single certificate
 * disagrees with the ledger by a few paise and the reconciliation against Form
 * 26AS becomes a manual exercise for the sake of a figure nobody wanted.
 *
 * Rounding half away from zero, matching the rest of this package rather than
 * JavaScript's Math.round, which rounds -0.5 to -0 and breaks symmetry on
 * credit notes.
 */
export function withholdingOn(base, ratePercent) {
    if (!Number.isFinite(ratePercent) || ratePercent < 0) {
        throw new Error('withholdingOn() needs a rate of zero or more');
    }
    const exact = percentOf(base, ratePercent);
    const minorPerUnit = 100;
    const rupees = roundHalfAwayFromZero(exact.minor / minorPerUnit);
    return fromMinor(rupees * minorPerUnit, base.currency);
}
/**
 * What a customer is expected to deduct on this invoice - a SUGGESTION.
 *
 * Never the stored figure. The deductor decides what they deduct: they may use
 * a different section, apply a threshold Fint cannot see because it aggregates
 * across invoices Fint does not have, or simply be wrong. Recording what
 * actually arrived is the only version that reconciles with Form 26AS, so this
 * is offered beside the field and the field is what gets saved.
 */
export function suggestedTds(doc, ratePercent) {
    return withholdingOn(tdsBaseFor(doc), ratePercent);
}
//# sourceMappingURL=withholding.js.map